import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Table, Badge, Spinner, Toast } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getShopByOwner, getShopOrders, updateOrderStatus } from '../../services/shopService';

const Orders = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [shop, setShop] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');

  useEffect(() => {
    loadShopAndOrders();
  }, [currentUser]);

  const loadShopAndOrders = async () => {
    try {
      setLoading(true);
      const shops = await getShopByOwner(currentUser.uid);
      if (shops.length > 0) {
        setShop(shops[0]);
        const ordersList = await getShopOrders(shops[0].id);
        // Ordina gli ordini per data di creazione (più recenti prima)
        const sortedOrders = ordersList.sort((a, b) => 
          new Date(b.createdAt) - new Date(a.createdAt)
        );
        setOrders(sortedOrders);
      }
    } catch (error) {
      console.error('Errore nel caricamento degli ordini:', error);
      setError(t('errors.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      await updateOrderStatus(shop.id, orderId, newStatus);
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order.id === orderId 
            ? { ...order, status: newStatus }
            : order
        )
      );
      setToastMessage('Stato ordine aggiornato con successo');
      setToastVariant('success');
      setShowToast(true);
    } catch (error) {
      console.error('Errore nell\'aggiornamento dello stato:', error);
      setToastMessage('Errore nell\'aggiornamento dello stato');
      setToastVariant('danger');
      setShowToast(true);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending_confirmation': { variant: 'warning', text: 'In attesa di conferma' },
      'confirmed': { variant: 'success', text: 'Confermato' },
      'processing': { variant: 'info', text: 'In elaborazione' },
      'shipped': { variant: 'primary', text: 'Spedito' },
      'delivered': { variant: 'success', text: 'Consegnato' },
      'cancelled': { variant: 'danger', text: 'Annullato' }
    };

    const config = statusConfig[status] || { variant: 'secondary', text: status };
    return (
      <Badge bg={config.variant} className="px-3 py-2">
        {config.text}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (!shop) {
    return (
      <Container className="py-5">
        <Card>
          <Card.Body className="text-center">
            <h3>{t('errors.noShopFound')}</h3>
            <Button variant="primary" onClick={() => navigate('/seller/settings')}>
              {t('actions.createShop')}
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Toast 
        show={showToast} 
        onClose={() => setShowToast(false)} 
        message={toastMessage}
        variant={toastVariant}
      />

      <h2 className="mb-4">Ordini Ricevuti</h2>

      <Card>
        <Card.Body>
          {orders.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-inbox" style={{ fontSize: 48, color: '#e0e0e0' }}></i>
              <p className="text-muted mt-3">Nessun ordine ricevuto</p>
            </div>
          ) : (
            <div>
              {orders.map(order => (
                <Card key={order.id} className="mb-3 border-0 shadow-sm">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div>
                        <h6 className="mb-1">Ordine #{order.orderNumber?.toString().padStart(6, '0')}</h6>
                        <p className="text-muted small mb-0">{order.customerName}</p>
                      </div>
                      {getStatusBadge(order.status)}
                    </div>
                    
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        <p className="text-muted small mb-0">Data</p>
                        <p className="mb-0">{formatDate(order.createdAt)}</p>
                      </div>
                      <div className="text-end">
                        <p className="text-muted small mb-0">Totale</p>
                        <p className="mb-0 fw-bold">€{order.total.toFixed(2)}</p>
                      </div>
                    </div>

                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="flex-grow-1"
                        onClick={() => navigate(`/seller/orders/${order.id}`)}
                      >
                        <i className="bi bi-eye me-2"></i>
                        Dettagli
                      </Button>
                      
                      {order.status === 'pending_confirmation' && (
                        <>
                          <Button
                            variant="outline-success"
                            size="sm"
                            className="flex-grow-1"
                            onClick={() => handleStatusUpdate(order.id, 'confirmed')}
                          >
                            <i className="bi bi-check-lg me-2"></i>
                            Conferma
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="flex-grow-1"
                            onClick={() => handleStatusUpdate(order.id, 'cancelled')}
                          >
                            <i className="bi bi-x-lg me-2"></i>
                            Annulla
                          </Button>
                        </>
                      )}
                      {order.status === 'confirmed' && (
                        <Button
                          variant="outline-info"
                          size="sm"
                          className="flex-grow-1"
                          onClick={() => handleStatusUpdate(order.id, 'processing')}
                        >
                          <i className="bi bi-gear me-2"></i>
                          Elabora
                        </Button>
                      )}
                      {order.status === 'processing' && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="flex-grow-1"
                          onClick={() => handleStatusUpdate(order.id, 'shipped')}
                        >
                          <i className="bi bi-truck me-2"></i>
                          Spedisci
                        </Button>
                      )}
                      {order.status === 'shipped' && (
                        <Button
                          variant="outline-success"
                          size="sm"
                          className="flex-grow-1"
                          onClick={() => handleStatusUpdate(order.id, 'delivered')}
                        >
                          <i className="bi bi-check-circle me-2"></i>
                          Consegnato
                        </Button>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Orders; 